import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function SystemSettings() {
  const styles = useThemeStyles();
  const [coupleOpen, setCoupleOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPlatformStatus();
  }, []);

  const loadPlatformStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/system-settings/platform-status');
      setCoupleOpen(response.data.coupleOpen !== false);
      setSupplierOpen(response.data.supplierOpen !== false);
    } catch (err) {
      setError('Failed to load platform status');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (side, newStatus) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = side === 'couple' ? { coupleOpen: newStatus } : { supplierOpen: newStatus };
      await api.put('/api/admin/system-settings/platform-status', payload);
      if (side === 'couple') setCoupleOpen(newStatus);
      else setSupplierOpen(newStatus);
      setSuccess(`${side === 'couple' ? 'Couples' : 'Suppliers'} ${newStatus ? 'opened' : 'closed'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update platform status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          System Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Platform Status
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Control access to login and registration separately for couples and suppliers.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={supplierOpen}
                onChange={(e) => handleToggle('supplier', e.target.checked)}
                disabled={saving}
                color="primary"
                size="large"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Supplier side {supplierOpen ? 'Open' : 'Closed'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {supplierOpen
                    ? 'Suppliers can register and login at /supplier/login'
                    : 'Supplier registration and login are blocked.'}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', display: 'block', mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={coupleOpen}
                onChange={(e) => handleToggle('couple', e.target.checked)}
                disabled={saving}
                color="primary"
                size="large"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Couple side {coupleOpen ? 'Open' : 'Closed'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {coupleOpen
                    ? 'Couples can register and login at /login'
                    : 'Couple registration and login are blocked.'}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', display: 'block' }}
          />
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> When closed, the blocked routes show a maintenance message. Admin access is always available.
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
          <li>Couple <code>/login</code>, <code>/register</code></li>
          <li>Supplier <code>/supplier/login</code>, <code>/supplier/register</code></li>
        </Box>
      </Alert>

    </Box>
  );
}

export default SystemSettings;
